import type { ColumnDef } from "@tanstack/react-table";
import { ExternalLink } from "lucide-react";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Account } from "@/hooks/api/use-accounts";
import type { ExternalConnectionLinks } from "@/hooks/api/use-external-connection-links";
import {
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_PRIMARY_COLUMN_WIDTHS,
} from "@/lib/column-defaults";
import { formatDate } from "@/lib/date-formatting";
import {
  getExternalConnectionLink,
  getExternalSystemName,
} from "@/lib/external-connection-links";
import type { CustomFieldDefinition } from "@/types/custom-fields";
import type { UnifiedTableColumn } from "@/types/table-column";
import { createCustomFieldColumns } from "./custom-field-column-utils";

export const getAccountColumns = (
  filterOptions?: {
    type?: string[];
    record_type_name?: string[];
  },
  customFields?: CustomFieldDefinition[],
  connections?: ExternalConnectionLinks[],
  isLoadingConnections?: boolean,
): UnifiedTableColumn<Account>[] => {
  // Start with standard columns
  const standardColumns: UnifiedTableColumn<Account>[] = [
    {
      key: "name",
      title: "Account Name",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      required: true, // This is the primary column that cannot be hidden
      size: DEFAULT_PRIMARY_COLUMN_WIDTHS.MEDIUM,
      render: (account: Account) => account.name,
    },
    {
      key: "type",
      title: "Account Type",
      sortable: true,
      filterable: true,
      filterType: "select",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 140, // Fixed
      filterOptions:
        filterOptions?.type?.map((type) => ({
          id: type,
          label: type,
        })) || [],
      render: (account: Account) => account.type || "-",
    },
    {
      key: "record_type_name",
      title: "Record Type",
      sortable: true,
      filterable: true,
      filterType: "select",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 130, // Fixed
      filterOptions:
        filterOptions?.record_type_name?.map((name) => ({
          id: name,
          label: name,
        })) || [],
      render: (account: Account) => account.record_type_name || "-",
    },
    {
      key: "website",
      title: "Website",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 200, // Fixed
      render: (account: Account) => {
        if (!account.website) return "-";

        return (
          <a
            href={
              account.website.startsWith("http")
                ? account.website
                : `https://${account.website}`
            }
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 hover:underline text-blue-600 dark:text-blue-400"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="truncate max-w-[180px]">{account.website}</span>
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
          </a>
        );
      },
      exportValue: (account: Account) => account.website || "",
    },
    {
      key: "created_date",
      title: "Created Date",
      sortable: true,
      filterable: true,
      filterType: "date",
      defaultSortDirection: "desc",
      nullSort: "min",
      size: 120, // Fixed
      render: (account: Account) =>
        account.created_date ? formatDate(account.created_date) : "-",
      exportValue: (account: Account) =>
        account.created_date ? formatDate(account.created_date) : "-",
    },
    {
      key: "contact_count",
      title: "# Contacts",
      sortable: true,
      filterable: true,
      filterType: "number",
      defaultSortDirection: "desc",
      nullSort: "min",
      size: 100, // Fixed
      render: (account: Account) => account.contact_count?.toString() || "",
    },
    {
      key: "opportunity_count",
      title: "# Opportunities",
      sortable: true,
      filterable: true,
      filterType: "number",
      defaultSortDirection: "desc",
      nullSort: "min",
      size: 120, // Fixed
      render: (account: Account) => account.opportunity_count?.toString() || "",
    },
    {
      key: "won_opportunity_count",
      title: "# Won Opps",
      sortable: true,
      filterable: true,
      filterType: "number",
      defaultSortDirection: "desc",
      nullSort: "min",
      size: 110, // Fixed
      render: (account: Account) =>
        account.won_opportunity_count?.toString() || "",
    },
    {
      key: "open_opportunity_count",
      title: "# Open Opps",
      sortable: true,
      filterable: true,
      filterType: "number",
      defaultSortDirection: "desc",
      nullSort: "min",
      size: 110, // Fixed
      render: (account: Account) =>
        account.open_opportunity_count?.toString() || "",
    },
    // Touchpoint metric columns - use metric_ prefix for consistent filter handling
    {
      key: "metric_touchpoint_count",
      title: "# Touchpoints",
      tooltip: "Count of touchpoints. Respects touchpoint filters.",
      columnType: "metric",
      category: "Touchpoint",
      categoryStyle: CATEGORY_STYLES.Touchpoint?.color,
      sortable: true,
      filterable: true,
      filterType: "number",
      defaultSortDirection: "desc",
      nullSort: "min",
      size: 140, // Fixed
      render: (account: Account) => account.touchpoint_count?.toString() || "0",
      exportValue: (account: Account) =>
        account.touchpoint_count?.toString() || "0",
    },
    {
      key: "metric_first_touchpoint_date",
      title: "First Touchpoint Date",
      tooltip: "Date of the first touchpoint. Respects touchpoint filters.",
      columnType: "metric",
      category: "Touchpoint",
      categoryStyle: CATEGORY_STYLES.Touchpoint?.color,
      sortable: true,
      filterable: true,
      filterType: "date",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 180, // Fixed
      defaultHidden: true, // Hidden by default, users can enable
      render: (account: Account) =>
        account.first_touchpoint_date
          ? formatDate(account.first_touchpoint_date)
          : "-",
      exportValue: (account: Account) =>
        account.first_touchpoint_date
          ? formatDate(account.first_touchpoint_date)
          : "",
    },
    {
      key: "metric_last_touchpoint_date",
      title: "Last Touchpoint Date",
      tooltip: "Date of the last touchpoint. Respects touchpoint filters.",
      columnType: "metric",
      category: "Touchpoint",
      categoryStyle: CATEGORY_STYLES.Touchpoint?.color,
      sortable: true,
      filterable: true,
      filterType: "date",
      defaultSortDirection: "desc",
      nullSort: "last",
      size: 180, // Fixed
      defaultHidden: true, // Hidden by default, users can enable
      render: (account: Account) =>
        account.last_touchpoint_date
          ? formatDate(account.last_touchpoint_date)
          : "-",
      exportValue: (account: Account) =>
        account.last_touchpoint_date
          ? formatDate(account.last_touchpoint_date)
          : "",
    },
    {
      key: "source_record_id",
      title: "Source Record ID",
      sortable: false,
      filterable: true,
      filterType: "text",
      size: 150, // Fixed
      render: (account: Account) => {
        if (isLoadingConnections) {
          return <Skeleton className="h-4 w-4" />;
        }

        const url = getExternalConnectionLink(account, connections);
        const systemName = getExternalSystemName(
          account.source_record_type || "",
        );

        if (!url) {
          return (
            <span className="text-muted-foreground">
              {account.source_record_id || "-"}
            </span>
          );
        }

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <span>{account.source_record_id}</span>
                <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
              </a>
            </TooltipTrigger>
            <TooltipContent>
              <p>View in {systemName}</p>
            </TooltipContent>
          </Tooltip>
        );
      },
      exportValue: (account: Account) => account.source_record_id || "",
    },
  ];

  // Append custom field columns if provided
  if (customFields && customFields.length > 0) {
    const customFieldColumns = createCustomFieldColumns(customFields);
    return [
      ...standardColumns,
      ...customFieldColumns,
    ] as UnifiedTableColumn<Account>[];
  }

  return standardColumns;
};

// Convert UnifiedTableColumn to TanStack Table ColumnDef
export const getAccountColumnDefs = (
  filterOptions?: {
    type?: string[];
    record_type_name?: string[];
  },
  customFields?: CustomFieldDefinition[],
  connections?: ExternalConnectionLinks[],
  isLoadingConnections?: boolean,
): ColumnDef<Account>[] => {
  const columns = getAccountColumns(
    filterOptions,
    customFields,
    connections,
    isLoadingConnections,
  );

  return columns
    .filter((col) => !col.exportOnly)
    .map((col) => {
      // For each column, create the column definition
      return {
        id: col.key.toString(),
        accessorKey: col.key,
        header: col.title,
        cell: ({ row }) => {
          const accountData = row.original;
          if (!accountData) {
            return "-"; // Handle cases where row data might be undefined
          }
          return col.render
            ? col.render(accountData)
            : String(accountData[col.key as keyof Account] || "");
        },
        meta: {
          title: col.title,
          tooltip: col.tooltip, // For metric column tooltips
          categoryStyle: col.categoryStyle, // For metric column styling
          sortable: col.sortable,
          filterable: col.filterable,
          filterType: col.filterType,
          filterOptions: col.filterOptions,
          defaultSortDirection: col.defaultSortDirection,
          nullSort: col.nullSort,
          isCustomField: col.isCustomField,
          customFieldDataType: col.customFieldDataType,
          dataType: col.dataType, // Include simple data type string
          required: col.required, // Pass through the required flag
        },
        size: col.size || DEFAULT_COLUMN_WIDTH, // Use column-specific size or default
      };
    });
};
